import React from 'react';
import { Document, Text } from '@react-pdf/renderer';
import { AccountLedgerLine, AccountLedgerReport } from '../../../types/finance';
import { formatDrCr, formatPaisa } from '../../../utils/paisa';
import {
  Page, FilterStrip, StatStrip, DataTable, fmtDate,
  type DataTableColumn, type SchoolBranding,
} from '../../../utils/pdf';
import { Amount, Description, figureWidth, leadColumns } from './movementCells';

interface Props {
  report: AccountLedgerReport;
  currency: string;
  branding: SchoolBranding;
  generatedAt: string;
}

/**
 * An account's ledger — every line the Ledger screen's filters match, with
 * the balance after each, between the balance before the dates and the
 * balance at the end. Under a group account, each line names the account
 * it is on instead of the accounts it was posted against.
 */
const AccountLedgerDocument: React.FC<Props> = ({ report, currency, branding, generatedAt }) => {
  const { account, items, totals } = report;
  const drCr = (paisa: number) => formatDrCr(paisa, currency);
  const title = `Ledger — ${account.code} ${account.name}`;
  const sideW = figureWidth(items.flatMap(l => [l.debitPaisa, l.creditPaisa]).map(p => formatPaisa(p, currency)));
  const balanceW = figureWidth([report.openingPaisa, report.closingPaisa, ...items.map(l => l.balancePaisa)].map(drCr), 96);
  const columns: Array<DataTableColumn<AccountLedgerLine>> = [
    ...leadColumns<AccountLedgerLine>(),
    { key: 'description', header: 'Description', flex: 1.5, render: l => <Description text={l.description || l.memo} status={l.status} isReversal={l.isReversal} /> },
    {
      key: 'other', header: account.isGroup ? 'Account' : 'Against', flex: 1.25,
      render: l => (account.isGroup ? (l.account ? [l.account] : []) : l.against).map(a => `${a.code} ${a.name}`).join(', '),
    },
    { key: 'debit', header: 'Debit', width: sideW, align: 'right', render: l => <Amount paisa={l.debitPaisa} currency={currency} /> },
    { key: 'credit', header: 'Credit', width: sideW, align: 'right', render: l => <Amount paisa={l.creditPaisa} currency={currency} /> },
    { key: 'balance', header: 'Balance', width: balanceW, align: 'right', render: l => <Text>{drCr(l.balancePaisa)}</Text> },
  ];

  return (
    <Document title={title} author={branding.name}>
      <Page
        branding={branding}
        orientation="landscape"
        meta={{ title, subtitle: report.filterSummary, generatedAt }}
        official={{ details: [
          { label: 'Records', value: String(items.length) },
          { label: 'Generated', value: fmtDate(generatedAt) },
        ] }}
      >
        <FilterStrip summary={report.filterSummary} />
        <StatStrip
          dense
          items={[
            { label: 'Opening balance', value: drCr(report.openingPaisa), tone: 'primary' },
            { label: 'Debits', value: formatPaisa(totals.debitPaisa, currency), tone: 'success' },
            { label: 'Credits', value: formatPaisa(totals.creditPaisa, currency), tone: 'danger' },
            { label: 'Closing balance', value: drCr(report.closingPaisa), tone: 'primary' },
          ]}
        />
        <DataTable
          columns={columns}
          rows={items}
          zebra
          compact
          small
          emptyText="Nothing was posted to this account in the window."
          summaryRows={[
            { kind: 'balance', before: true, label: 'Opening balance', values: { balance: drCr(report.openingPaisa) } },
            { kind: 'balance', label: 'Closing balance', values: { balance: drCr(report.closingPaisa) } },
          ]}
        />
      </Page>
    </Document>
  );
};

export default AccountLedgerDocument;
